// Pure helpers for filtering the grouped-by-project observation map shown on
// the Observations page. Extracted from ObservationList so the behavior is
// unit-testable and so we can detect when the observatory filter is silently
// hiding observations the user otherwise expects to see (the "I can't find it
// in the Observations area" bug — the page auto-selects a saved observatory
// and quietly drops every observation assigned elsewhere).
//
// `grouped` is shaped { [projectId]: Observation[] }, matching the component's
// `groupedObservations` state. Observations carry a populated `assigned_to`
// ({ _id | id }) and a `status` string.

const activeStatusesFrom = (statusFilters) =>
  Object.entries(statusFilters || {})
    .filter(([, enabled]) => enabled)
    .map(([status]) => status);

// True when the observation belongs to the currently-selected observatory.
// `selectedObservatory === 'all'` matches everything.
const matchesObservatory = (obs, selectedObservatory) => {
  if (selectedObservatory === 'all') return true;
  return (
    obs.assigned_to?._id === selectedObservatory ||
    obs.assigned_to?.id === selectedObservatory
  );
};

// Apply the project / status / observatory filters, dropping any project whose
// observations are all filtered out. Mirrors the original inline logic.
export const filterGroupedObservations = (
  grouped,
  { selectedObservatory = 'all', statusFilters = {}, projectFilter = null } = {}
) => {
  const activeStatuses = activeStatusesFrom(statusFilters);
  const filtered = {};

  Object.entries(grouped || {}).forEach(([projectId, observations]) => {
    if (projectFilter && projectId !== projectFilter) return;

    const filteredObs = (observations || []).filter((obs) => {
      if (!activeStatuses.includes(obs.status)) return false;
      return matchesObservatory(obs, selectedObservatory);
    });

    if (filteredObs.length > 0) {
      filtered[projectId] = filteredObs;
    }
  });

  return filtered;
};

// How many observations pass the status (and project) filters but are removed
// *solely* by the observatory filter. Returns 0 when viewing all observatories.
// Drives the "N observations on other observatories are hidden" banner.
export const countHiddenByObservatory = (
  grouped,
  { selectedObservatory = 'all', statusFilters = {}, projectFilter = null } = {}
) => {
  if (selectedObservatory === 'all') return 0;
  const activeStatuses = activeStatusesFrom(statusFilters);
  let hidden = 0;

  Object.entries(grouped || {}).forEach(([projectId, observations]) => {
    if (projectFilter && projectId !== projectFilter) return;
    (observations || []).forEach((obs) => {
      if (!activeStatuses.includes(obs.status)) return;
      if (!matchesObservatory(obs, selectedObservatory)) hidden += 1;
    });
  });

  return hidden;
};
